// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { nativeScrollViewProps } from 'components/common/scroll/TreactNativeScrollStyles';
import { Contract } from 'model/Contract';
import { TFunction } from 'i18next';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import sortBy from 'lodash/sortBy';
import { Button, Table } from '@tls/treact-ui';
import { FlagInformation } from '@tls/treact-ui/flags';
import { IconPreview } from '@tls/treact-ui/icons';
import HighlightText from 'components/common/HighlightText';
import ContractDetailsDialog from './ContractDetailsDialog';
import { contactLines } from './contactLines';
import TestableLoader from 'components/treactui-template/atoms/TestableLoader';

const InformationFlag = FlagInformation;

// Contract.feature is the licensed permission id; map it to a human label, else fall back to the material number.
const FEATURE_LABEL_KEYS: Record<string, string> = {
  CreateQCProject: 'settings.contracts.features.CreateQCProject',
  CreateCDProject: 'settings.contracts.features.CreateCDProject',
  PreviewFeatures: 'settings.contracts.features.PreviewFeatures',
  ExportVliTai: 'settings.contracts.features.ExportVliTai',
  MultiLabelCD: 'settings.contracts.features.MultiLabelCD',
};

function featureLabel(contract: Contract, t: TFunction): string {
  if (!contract.feature) return contract.materialNumber;
  // Feature may list several permission ids separated by newlines; translate each line, keep unknown ones as-is.
  return contract.feature
    .split(/\r?\n/)
    .map(part => {
      const key = FEATURE_LABEL_KEYS[part.trim()];
      return key ? t(key) : part;
    })
    .join('\n');
}

export default function ContractsTable({
  data,
  isLoading,
  filterText,
  hideContact = false,
  hideMaterialNumber = false,
}: Readonly<Props>) {
  const { t } = useTranslation();

  const [selectedContract, setSelectedContract] = useState<Contract | undefined>(undefined);
  const closeDetails = useCallback(() => setSelectedContract(undefined), []);

  const generateContractRow = useCallback(
    (contract: Contract) => {
      const featureName = featureLabel(contract, t);
      const materialNumber = contract.materialNumber.replace(/^0+/, ''); // strip leading zeros
      return (
        <Table.Row
          key={`${contract.contractId}:${contract.customerNumber}:${contract.endCustomerNumber}`}
          data-testid={`settings-contract-${contract.contractId}-${contract.customerNumber}-${contract.endCustomerNumber}-row`}
        >
          <MultilineCell style={{ minWidth: '25ch' }} width={1} alignment='LEFT'>
            <FeatureContainer>
              <FeatureName>
                <HighlightText text={featureName} query={filterText} />
              </FeatureName>
              {contract.description && (
                <PreLineText>
                  <HighlightText text={contract.description} query={filterText} />
                </PreLineText>
              )}
              {!hideMaterialNumber && materialNumber && (
                <span>
                  <HighlightText text={`${materialNumber}`} query={filterText} />
                </span>
              )}
            </FeatureContainer>
          </MultilineCell>
          {!hideContact && (
            <MultilineCell width={1} alignment='LEFT'>
              <ContactContainer>
                {contactLines(contract).map((line, index) => (
                  <span key={`${index}:${line}`}>
                    <HighlightText text={line} query={filterText} />
                  </span>
                ))}
              </ContactContainer>
            </MultilineCell>
          )}
          <Table.Cell width={1} alignment='LEFT'>
            <HighlightText text={contract.startDate?.toLocaleDateString()} query={filterText} />
          </Table.Cell>
          <Table.Cell width={1} alignment='LEFT'>
            <HighlightText text={contract.endDate?.toLocaleDateString()} query={filterText} />
          </Table.Cell>
          <Table.Cell width={1} alignment='LEFT'>
            <HighlightText text={contract.status} query={filterText} />
          </Table.Cell>
          <Table.Cell style={{ minWidth: '4rem' }} width={0} alignment='CENTER'>
            <Button
              variant='transparent'
              icon={<IconPreview />}
              noShadow
              noBorder
              onClick={() => setSelectedContract(contract)}
              data-testid={`settings-contract-${contract.contractId}-${contract.customerNumber}-${contract.endCustomerNumber}-details-button`}
            />
          </Table.Cell>
        </Table.Row>
      );
    },
    [filterText, t, hideContact, hideMaterialNumber]
  );

  const filteredData = useMemo(() => {
    const filter = filterText?.toLocaleLowerCase();
    return data?.filter(d => {
      if (!filter) return true;
      const contains = (value?: string | null) => value?.toLocaleLowerCase().includes(filter) ?? false;
      return (
        contains(d.materialNumber) ||
        contains(d.feature) ||
        contains(featureLabel(d, t)) ||
        contains(d.description) ||
        contains(d.contractId) ||
        contains(d.customerNumber) ||
        contains(d.endCustomerNumber) ||
        contains(d.zeCustomerNumber) ||
        contains(d.contactName) ||
        contains(d.contactCountry) ||
        contains(d.contactEmail) ||
        (d.contactAddressLines ?? []).some(line => contains(line)) ||
        contains(d.status) ||
        contains(d.startDate.toLocaleDateString()) ||
        contains(d.endDate.toLocaleDateString()) ||
        contains(d.type)
      );
    });
  }, [data, filterText, t]);

  const sortArray = useCallback((array: Contract[], sortProp: string) => sortBy(array, sortProp), []);

  const onSortByFeature = useCallback((array: Contract[]) => sortArray(array, 'feature'), [sortArray]);
  const onSortByContact = useCallback((array: Contract[]) => sortArray(array, 'contactName'), [sortArray]);
  const onSortByStatus = useCallback((array: Contract[]) => sortArray(array, 'status'), [sortArray]);
  const onSortByStartDate = useCallback((array: Contract[]) => sortArray(array, 'startDate'), [sortArray]);
  const onSortByEndDate = useCallback((array: Contract[]) => sortArray(array, 'endDate'), [sortArray]);

  return (
    <>
      <Table list={filteredData ?? []}>
        {(contracts: Contract[]) => {
          const bodyRows = (
            <>
              {isLoading && <TestableLoader label='' data-testid='settings-contracts-loader' />}
              {!isLoading && contracts.length < 1 && (
                <Table.Row>
                  <Table.Cell width='100%' alignment={'CENTER'}>
                    <NoEntryContainer data-testid='settings-contracts-empty-state'>
                      <InformationFlag width={'1rem'} />
                      {t('settings.contracts.noContracts')}
                    </NoEntryContainer>
                  </Table.Cell>
                </Table.Row>
              )}
              {contracts.map(generateContractRow)}
            </>
          );
          return (
            <>
              <Table.Header>
                <Table.HeaderRow>
                  <Table.HeaderSortCell
                    style={{ minWidth: '25ch' }}
                    width={1}
                    sortFn={onSortByFeature}
                    sortKey='feature'
                    data-testid='settings-contracts-feature-sort-button'
                  >
                    {t('settings.contracts.feature')}
                  </Table.HeaderSortCell>
                  {!hideContact && (
                    <Table.HeaderSortCell
                      width={1}
                      sortFn={onSortByContact}
                      sortKey='contactName'
                      data-testid='settings-contracts-customer-sort-button'
                    >
                      {t('settings.contracts.zeCustomer')}
                    </Table.HeaderSortCell>
                  )}
                  <Table.HeaderSortCell
                    width={1}
                    sortFn={onSortByStartDate}
                    sortKey='startDate'
                    data-testid='settings-contracts-start-date-sort-button'
                  >
                    {t('settings.contracts.startDate')}
                  </Table.HeaderSortCell>
                  <Table.HeaderSortCell
                    width={1}
                    sortFn={onSortByEndDate}
                    sortKey='endDate'
                    data-testid='settings-contracts-end-date-sort-button'
                  >
                    {t('settings.contracts.endDate')}
                  </Table.HeaderSortCell>
                  <Table.HeaderSortCell
                    width={1}
                    sortFn={onSortByStatus}
                    sortKey='status'
                    data-testid='settings-contracts-status-sort-button'
                  >
                    {t('settings.contracts.status')}
                  </Table.HeaderSortCell>
                  <Table.HeaderCell style={{ minWidth: '4rem' }} width={0}>
                    {t('settings.contracts.details')}
                  </Table.HeaderCell>
                </Table.HeaderRow>
              </Table.Header>
              <Table.ScrollingBody scrollViewProps={nativeScrollViewProps}>{bodyRows}</Table.ScrollingBody>
            </>
          );
        }}
      </Table>
      <ContractDetailsDialog contract={selectedContract} onClose={closeDetails} />
    </>
  );
}

interface Props {
  data: Contract[] | undefined;
  isLoading: boolean;
  filterText?: string;
  hideContact?: boolean; // hide the customer column (shown in the details dialog instead)
  hideMaterialNumber?: boolean; // hide the material number under the feature name
}

const NoEntryContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ContactContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-wrap: anywhere;
`;

const FeatureContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const FeatureName = styled.span`
  font-weight: bold;
  font-size: 1.1em;
  margin-bottom: 0.3rem;
  white-space: pre-line; /* keep one feature per line for multi-feature contracts */
`;

// Body cells are fixed to 3.875rem and clamp at 3 lines; multi-line cells grow with their content instead.
const MultilineCell = styled(Table.Cell)`
  height: auto;
  min-height: 3.875rem;
  padding: 0.5rem 0;

  & > div {
    display: block;
    -webkit-line-clamp: unset;
    overflow: visible;
  }
`;

const PreLineText = styled.span`
  white-space: pre-line;
`;
